import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { useLabels } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Lock } from "lucide-react";

/** Supplier master. Non-admins see only the code + public label — the real
 * company name and contact details are admin-only (server strips them too). */
export default function AdminSuppliers() {
  const qc = useQueryClient();
  const L = useLabels();
  const { data = [] } = useQuery({
    queryKey: ["suppliers-all"],
    queryFn: async () => (await api.get("/suppliers")).data,
  });
  const empty = { code: "", name: "", real_name: "", gstin: "", contact_person: "", phone: "", email: "", address: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const payload = L.isAdmin ? form : { code: form.code, name: form.name };
      await api.post("/suppliers", payload);
      toast.success(`${L.Source} created`);
      setForm(empty);
      qc.invalidateQueries({ queryKey: ["suppliers-all"] });
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (s) => {
    try {
      await api.patch(`/suppliers/${s.id}`, { active: !s.active });
      qc.invalidateQueries({ queryKey: ["suppliers-all"] });
    } catch (e) { toast.error(formatError(e)); }
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-suppliers">
      <div>
        <div className="data-label">Master</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">{L.Sources}</h1>
        {L.isAdmin && (
          <p className="text-sm text-muted-foreground mt-1">
            Staff only ever see the <b>code</b> and <b>label</b>. Real name and contacts are visible to admin alone.
          </p>
        )}
      </div>
      <div className="bg-white border border-border rounded-sm p-4 grid md:grid-cols-4 gap-2 items-end">
        <input className="field-sm font-mono" placeholder="Code (e.g. ABLE)" value={form.code}
               onChange={set("code")} data-testid="sup-code" />
        <input className="field-sm" placeholder={L.isAdmin ? "Label staff see (e.g. ABLE)" : "Label"} value={form.name}
               onChange={set("name")} data-testid="sup-name" />
        {L.isAdmin && (
          <>
            <input className="field-sm" placeholder="Real company name (admin only)" value={form.real_name}
                   onChange={set("real_name")} data-testid="sup-real-name" />
            <input className="field-sm font-mono" placeholder="GSTIN" value={form.gstin}
                   onChange={set("gstin")} data-testid="sup-gstin" />
            <input className="field-sm" placeholder="Contact person" value={form.contact_person}
                   onChange={set("contact_person")} data-testid="sup-contact" />
            <input className="field-sm" placeholder="Phone" value={form.phone}
                   onChange={set("phone")} data-testid="sup-phone" />
            <input className="field-sm" placeholder="Email" value={form.email}
                   onChange={set("email")} data-testid="sup-email" />
            <input className="field-sm" placeholder="Address" value={form.address}
                   onChange={set("address")} data-testid="sup-address" />
          </>
        )}
        <button onClick={create} disabled={busy || !form.code || !form.name} className="btn-primary"
                data-testid="sup-create">
          <Plus className="w-4 h-4 mr-1" /> Add
        </button>
      </div>
      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="suppliers-list">
        {data.map(s => (
          <div key={s.id} className="p-3 flex items-center gap-3 flex-wrap">
            <span className="container-code text-primary">{s.code}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{s.name}</div>
              {L.isAdmin && (s.real_name || s.contact_person || s.phone) && (
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  {[s.real_name, s.contact_person, s.phone].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
            {!s.active && <span className="badge-flat bg-muted text-muted-foreground">disabled</span>}
            <button onClick={() => toggle(s)} className="btn-ghost text-xs"
                    data-testid={`sup-toggle-${s.code}`}>{s.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
